import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_API_KEY, GET_API_KEYS, REVOKE_API_KEY } from '../../graphql';
import { useCopy } from '../../lib/copyToClipboard';
import { lastActiveLabel } from './accountAgents';

/**
 * Connections → API keys (Prototype v14): "New key", then rows with the key's
 * name and prefix, its scope, when it was last used, and Revoke. A new key is
 * shown once, to copy; only its prefix is kept (apiserver platform/services/
 * apiKeys.js). Admins make and revoke; the server refuses anyone else.
 */

interface ApiKey {
  id: string;
  label: string;
  prefix: string;
  scope: 'read' | 'readwrite' | string;
  createdAt: string;
  lastUsedAt?: string | null;
}

const SCOPE_WORDS: Record<string, string> = { read: 'Read only', readwrite: 'Read & write' };
const SCOPE_HELP: Record<string, string> = {
  read: 'Ask your archive (Research). Can’t change anything.',
  readwrite:
    'Send articles in, ask your archive, and create social posts (which still wait for approval on “Ask me first” channels).',
};

export function ApiKeysSection({ accountId }: { accountId: string }) {
  const { data, refetch } = useQuery<{ apiKeys: ApiKey[] }>(GET_API_KEYS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [create, { loading: creating }] = useMutation(CREATE_API_KEY);
  const [revoke] = useMutation(REVOKE_API_KEY);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [scope, setScope] = useState<'read' | 'readwrite'>('read');
  const [shown, setShown] = useState<{ label: string; token: string } | null>(null);
  const [error, setError] = useState('');
  const { copy, state } = useCopy();
  const keys = data?.apiKeys || [];

  const make = async () => {
    setError('');
    try {
      const { data: res } = await create({ variables: { accountId, label: label.trim(), scope } });
      const made = res?.createApiKey;
      if (made) setShown({ label: made.key.label, token: made.token });
      setOpen(false);
      setLabel('');
      await refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The key wasn’t made. Try again.');
    }
  };

  const drop = async (k: ApiKey) => {
    if (
      !window.confirm(
        `Revoke “${k.label}”? Anything using it stops working at once. This can’t be undone.`
      )
    )
      return;
    setError('');
    try {
      await revoke({ variables: { accountId, id: k.id } });
      await refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The key wasn’t revoked. Try again.');
    }
  };

  const copied = state('new-key');
  return (
    <>
      <div className="ink-conn-head">
        <h2>API keys</h2>
        {!open ? (
          <button type="button" className="ink-btn" onClick={() => setOpen(true)}>
            New key
          </button>
        ) : null}
      </div>
      {shown ? (
        <div className="ink-key-shown" role="status">
          <strong>Key for {shown.label} is ready. Copy it now: it’s shown only once.</strong>
          <div className="ink-key-shown-row">
            <code>{shown.token}</code>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={(e) => void copy('new-key', shown.token, e)}
            >
              {copied === 'copied' ? 'Copied' : copied === 'failed' ? 'Couldn’t copy' : 'Copy'}
            </button>
            <button type="button" className="ink-btn ink-btn-quiet" onClick={() => setShown(null)}>
              Done
            </button>
          </div>
        </div>
      ) : null}
      {open ? (
        <div className="ink-key-new">
          <label className="ink-site-label">
            Name
            <input
              className="ink-site-input"
              value={label}
              autoFocus
              maxLength={60}
              placeholder="e.g. Partner feed"
              onChange={(e) => setLabel(e.target.value)}
            />
          </label>
          <div className="ink-site-label">
            What it can do
            <span className="ink-seg" style={{ alignSelf: 'flex-start' }}>
              {(['read', 'readwrite'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  data-active={scope === s ? 'true' : undefined}
                  onClick={() => setScope(s)}
                >
                  {SCOPE_WORDS[s]}
                </button>
              ))}
            </span>
            <span className="ink-muted" style={{ fontSize: 12.5 }}>
              {SCOPE_HELP[scope]}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              onClick={() => {
                setOpen(false);
                setError('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              disabled={creating || !label.trim()}
              onClick={() => void make()}
            >
              {creating ? 'Making…' : 'Make key'}
            </button>
          </div>
        </div>
      ) : null}
      {error ? <p className="ink-lede">{error}</p> : null}
      <div className="ink-conn-keys">
        {keys.length === 0 ? (
          <div className="ink-bill-empty" style={{ padding: '15px 0' }}>
            No keys yet.
          </div>
        ) : (
          keys.map((k) => (
            <div className="ink-key-row" key={k.id}>
              <div style={{ minWidth: 0 }}>
                <div className="ink-key-name">{k.label}</div>
                <div className="ink-key-prefix">{k.prefix}••••</div>
              </div>
              <span className="ink-muted">{SCOPE_WORDS[k.scope] || k.scope}</span>
              <span className="ink-muted">
                {k.lastUsedAt ? `Used ${lastActiveLabel(k.lastUsedAt)}` : 'Not used yet'}
              </span>
              <button
                type="button"
                className="ink-bill-link"
                style={{ color: 'var(--ink-red)' }}
                onClick={() => void drop(k)}
              >
                Revoke
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
}
